<script>
  import { onMount } from 'svelte'
  import { getWeightRecords, deleteWeightRecord, calculateWeightChange } from '../services/db.js'
  import WeightForm from './WeightForm.svelte'
  import { navigateTo, syncStatus } from '../stores/app.js'

  let weightRecords = []
  let isLoading = true
  let showForm = false
  let formMode = 'create'
  let editingRecordId = null
  let previousSyncState = false

  onMount(async () => {
    await loadWeightRecords()
  })

  async function loadWeightRecords() {
    isLoading = true
    try {
      weightRecords = await getWeightRecords()
    } catch (error) {
      console.error('Failed to load weight records:', error)
    } finally {
      isLoading = false
    }
  }

  // Watch for sync completion (transition from syncing to not syncing)
  $: {
    const wasSyncing = previousSyncState;
    const isCurrentlySyncing = $syncStatus?.isSyncing || false;

    // Reload data when sync completes successfully
    if (wasSyncing && !isCurrentlySyncing && $syncStatus?.success) {
      loadWeightRecords();
    }

    previousSyncState = isCurrentlySyncing;
  }

  function handleAddRecord() {
    formMode = 'create'
    editingRecordId = null
    showForm = true
  }

  function handleEditRecord(recordId) {
    formMode = 'edit'
    editingRecordId = recordId
    showForm = true
  }

  async function handleDeleteRecord(record) {
    const dateStr = formatLocalDateTime(record.date)
    const confirmMessage = `確定要刪除這筆體重記錄嗎？\n\n日期：${dateStr}\n體重：${record.weight} kg\n\n此操作無法復原。`

    if (confirm(confirmMessage)) {
      try {
        await deleteWeightRecord(record.id)
        await loadWeightRecords()
      } catch (error) {
        console.error('Failed to delete weight record:', error)
        alert('刪除失敗，請稍後再試')
      }
    }
  }

  async function handleFormSave() {
    showForm = false
    await loadWeightRecords()
  }

  function handleFormClose() {
    showForm = false
  }

  // Format date in local timezone
  function formatLocalDateTime(dateStr) {
    const d = new Date(dateStr)
    const year = d.getFullYear()
    const month = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    const hours = String(d.getHours()).padStart(2, '0')
    const minutes = String(d.getMinutes()).padStart(2, '0')
    return `${year}/${month}/${day} ${hours}:${minutes}`
  }

  function getWeightChangeDisplay(record, index) {
    if (index >= weightRecords.length - 1) {
      return null // No previous record
    }

    const previousRecord = weightRecords[index + 1]
    const change = calculateWeightChange(record.weight, previousRecord.weight)

    if (change.direction === 'stable') {
      return { icon: '→', text: '持平', color: 'text-gray-500' }
    } else if (change.direction === 'up') {
      return { icon: '↑', text: `+${change.diff.toFixed(1)} kg`, color: 'text-orange-600' }
    } else {
      return { icon: '↓', text: `-${change.diff.toFixed(1)} kg`, color: 'text-green-600' }
    }
  }
</script>

<div class="container mx-auto px-2 py-8 pb-24">
  <!-- Header -->
  <div class="flex justify-between items-center mb-6">
    <h1 class="text-2xl font-bold text-gray-800">體重記錄</h1>
    <button on:click={handleAddRecord} class="btn btn-primary">
      新增記錄
    </button>
  </div>

  <!-- Loading State -->
  {#if isLoading}
    <div class="text-center py-12">
      <div class="spinner mx-auto mb-4"></div>
      <p class="text-gray-600">載入中...</p>
    </div>

  <!-- Empty State -->
  {:else if weightRecords.length === 0}
    <div class="text-center py-12">
      <div class="text-6xl mb-4">⚖️</div>
      <h2 class="text-xl font-semibold text-gray-700 mb-2">尚無體重記錄</h2>
      <p class="text-gray-600 mb-6">開始記錄你的體重，追蹤身體變化</p>
      <button on:click={handleAddRecord} class="btn btn-primary">
        新增第一筆記錄
      </button>
    </div>

  <!-- Records List -->
  {:else}
    <div class="space-y-4">
      {#each weightRecords as record, index (record.id)}
        {@const change = getWeightChangeDisplay(record, index)}
        <div class="card hover:shadow-md transition-shadow">
          <div class="flex justify-between items-start">
            <div class="flex-1">
              <!-- Date -->
              <div class="text-sm text-gray-600 mb-2">
                {formatLocalDateTime(record.date)}
              </div>

              <!-- Weight and Body Fat -->
              <div class="flex items-baseline gap-4 mb-2">
                <div>
                  <span class="text-2xl font-bold text-gray-800">{record.weight}</span>
                  <span class="text-sm text-gray-600 ml-1">kg</span>
                </div>
                {#if record.bodyFat}
                  <div>
                    <span class="text-lg font-semibold text-gray-700">{record.bodyFat}</span>
                    <span class="text-sm text-gray-600 ml-1">% 體脂</span>
                  </div>
                {/if}
              </div>

              <!-- Weight Change -->
              {#if change}
                <div class="flex items-center gap-1 {change.color} text-sm">
                  <span class="font-medium">{change.icon}</span>
                  <span>{change.text}</span>
                </div>
              {/if}
            </div>

            <!-- Action Buttons -->
            <div class="flex gap-2 ml-4">
              <button
                on:click={() => handleEditRecord(record.id)}
                class="text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                編輯
              </button>
              <button
                on:click={() => handleDeleteRecord(record)}
                class="text-red-600 hover:text-red-800 text-sm font-medium"
              >
                刪除
              </button>
            </div>
          </div>
        </div>
      {/each}
    </div>
  {/if}

  <!-- Future Features Hint -->
  <div class="mt-8 card bg-blue-50 border border-blue-200">
    <h2 class="text-lg font-semibold text-blue-900 mb-3">未來功能</h2>
    <ul class="text-sm text-blue-800 space-y-1">
      <li>📈 體重趨勢圖表</li>
      <li>💧 水分攝取記錄</li>
      <li>🍎 食物營養素記錄</li>
      <li>📊 資料匯出功能</li>
    </ul>
  </div>
</div>

<!-- Weight Form Modal -->
{#if showForm}
  <WeightForm
    mode={formMode}
    recordId={editingRecordId}
    on:save={handleFormSave}
    on:close={handleFormClose}
  />
{/if}
